export const USER_ROLES = ["admin", "conducteur", "poseur"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const TASK_TYPES = [
  "vt",
  "suivi_chantier",
  "reception_supports",
  "reception_chantier",
  "pv_reception",
  "levee_reserve",
  "sav",
] as const;
export type TaskType = (typeof TASK_TYPES)[number];

// Barème indemnité kilométrique (EUR/km) appliqué aux tournées.
// ponytail: constante, table de config si le barème évolue par période.
export const KM_INDEMNITY_RATE_EUR = 0.4;

export const TASK_TYPE_LABELS_FR: Record<TaskType, string> = {
  vt: "Visite technique",
  suivi_chantier: "Suivi de chantier",
  reception_supports: "Réception des supports",
  reception_chantier: "Réception chantier",
  pv_reception: "PV de réception",
  levee_reserve: "Levée de réserve",
  sav: "SAV",
};

export const POSEUR_TASK_TYPES = ["pv_reception", "levee_reserve", "sav"] as const;
export type PoseurTaskType = (typeof POSEUR_TASK_TYPES)[number];

export function isPoseurTaskType(type: TaskType): type is PoseurTaskType {
  return (POSEUR_TASK_TYPES as readonly TaskType[]).includes(type);
}

export const TASK_STATUSES = [
  "assigned",
  "accepted",
  "refused",
  "in_progress",
  "submitted",
  "completed",
  "cancelled",
  "questioned",
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_STATUS_LABELS_FR: Record<TaskStatus, string> = {
  assigned: "Proposition",
  accepted: "Acceptée",
  refused: "Refusée",
  in_progress: "En cours",
  submitted: "Soumise",
  completed: "Validée",
  cancelled: "Annulée",
  questioned: "Mitigé",
};

export const RENDER_REVIEW_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "questioned",
] as const;
export type RenderReviewStatus = (typeof RENDER_REVIEW_STATUSES)[number];

// `video` : ajouté avec le formulaire SAV (migration 20261009120000). Une
// vidéo n'est jamais transformée (imgproxy) ni embarquée dans un PDF : elle se
// consulte depuis la revue admin et se compte dans le compte rendu.
export const RENDER_MEDIA_KINDS = [
  "photo",
  "signature",
  "document",
  "video",
  "other",
] as const;
export type RenderMediaKind = (typeof RENDER_MEDIA_KINDS)[number];

// Un refus n'est pas la fin de la mission, c'est la fin d'une attribution :
// l'admin la repropose à un autre relais (refused → assigned) ou y renonce
// (refused → cancelled). Même chose depuis une annulation qu'on relance.
// Miroir exact de assert_task_transition côté base (migration 20260928150000).
export const ALLOWED_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  assigned: ["accepted", "refused", "cancelled"],
  accepted: ["in_progress", "refused", "cancelled", "assigned"],
  refused: ["assigned", "cancelled"],
  in_progress: ["submitted", "cancelled"],
  submitted: ["completed", "in_progress", "questioned"],
  completed: [],
  cancelled: ["assigned"],
  questioned: ["in_progress", "cancelled"],
};

export const ASSIGNMENT_OUTCOMES = [
  "refused",
  "reassigned",
  "unassigned",
  "cancelled",
  "completed",
] as const;
export type AssignmentOutcome = (typeof ASSIGNMENT_OUTCOMES)[number];

export const ASSIGNMENT_OUTCOME_LABELS_FR: Record<AssignmentOutcome, string> = {
  refused: "Refusée par le relais",
  reassigned: "Réattribuée à un autre relais",
  unassigned: "Retirée au relais",
  cancelled: "Mission annulée",
  completed: "Mission menée à terme",
};

export function canTransition(from: TaskStatus, to: TaskStatus): boolean {
  if (from === to) return true;
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export const SUPPORT_TYPES = [
  "decaisse",
  "dalle_beton",
  "technopieux",
  "nivellement",
  "vide_sanitaire",
  "raccordements_plomberie",
  "raccordements_electriques",
  "consuel",
  "autre",
] as const;
export type SupportType = (typeof SUPPORT_TYPES)[number];

export const SUPPORT_TYPE_LABELS_FR: Record<SupportType, string> = {
  decaisse: "Décaissé",
  dalle_beton: "Dalle Béton",
  technopieux: "Technopieux",
  nivellement: "Nivellement",
  vide_sanitaire: "Maçonnerie avec Vide Sanitaire",
  raccordements_plomberie: "Raccordements Plomberie",
  raccordements_electriques: "Raccordements Électriques Triphasés",
  consuel: "CONSUEL",
  autre: "Autre",
};

export const VT_PROJECT_KINDS = ["gk", "ccmi", "gx"] as const;
export type VtProjectKind = (typeof VT_PROJECT_KINDS)[number];

export const VT_PROJECT_KIND_LABELS_FR: Record<VtProjectKind, string> = {
  gk: "GK",
  ccmi: "CCMI",
  gx: "GX",
};

export const VT_RGA_LEVELS = ["faible", "modere", "eleve"] as const;
export type VtRgaLevel = (typeof VT_RGA_LEVELS)[number];

export const VT_RGA_LEVEL_LABELS_FR: Record<VtRgaLevel, string> = {
  faible: "Faible",
  modere: "Modéré",
  eleve: "Élevé",
};

// ── SAV ───────────────────────────────────────────────────────────────────
// Une intervention SAV n'a pas de liste de contrôle type : le poseur déclare
// ce qu'on lui a demandé de vérifier / réparer / diagnostiquer, puis ce qu'il
// en a fait. Les codes sont des mots simples : ils passent par
// `enumCodeSet()` pour ne pas être pris pour de la prose à traduire.
export const SAV_INTERVENTION_NATURES = ["verification", "reparation", "diagnostic"] as const;
export type SavInterventionNature = (typeof SAV_INTERVENTION_NATURES)[number];

export const SAV_INTERVENTION_NATURE_LABELS_FR: Record<SavInterventionNature, string> = {
  verification: "Vérification",
  reparation: "Réparation",
  diagnostic: "Diagnostic",
};

export const SAV_INTERVENTION_RESULTATS = ["realise", "partiel", "non_realise"] as const;
export type SavInterventionResultat = (typeof SAV_INTERVENTION_RESULTATS)[number];

export const SAV_INTERVENTION_RESULTAT_LABELS_FR: Record<SavInterventionResultat, string> = {
  realise: "Réalisé",
  partiel: "Partiellement réalisé",
  non_realise: "Non réalisé",
};

export const SAV_SUITES = ["aucune", "nouvelle_intervention", "devis", "autre"] as const;
export type SavSuite = (typeof SAV_SUITES)[number];

export const SAV_SUITE_LABELS_FR: Record<SavSuite, string> = {
  aucune: "Aucune suite à donner",
  nouvelle_intervention: "Nouvelle intervention à planifier",
  devis: "Devis à établir",
  autre: "Autre",
};
